import type { EmploymentCohort } from "@winche/shared";
import { useEffect, useMemo, useState } from "react";
import { Badge, Callout } from "@winche/ui";
import {
  Block,
  EmptyState,
  ErrorMessage,
  LoadButton,
  LoadingBlock,
  Metric,
  MetricGrid,
  Panel,
  SectionHeader,
  Segmented,
} from "../components/ui";
import type { AdminFailure } from "../lib/callable-error";
import { Chart, type ChartDatum } from "../components/chart";
import { DataTable, type Column } from "../components/table";
import { compactNum, dateTimeUtc, num, pct, relativeTime, shortDay, toMillis } from "../lib/format";

// Types mirrored from packages/functions/src/http/admin-insights.ts
export interface UsageGrowthPoint {
  date: string;
  signups: number;
  cumulative: number;
}

export interface UsageOnboardingFunnel {
  signup: number;
  step2Plus: number;
  step3Plus: number;
  completed: number;
  stuck: number;
  stuckByStep?: { step1: number; step2: number; step3: number };
}

export interface UsageActivation {
  completed: number;
  active7d: number;
  active30d: number;
  neverActive: number;
}

export interface UsageCohort {
  week: string;
  signup: number;
  step1: number;
  step2: number;
  step3: number;
  completed: number;
  active30d: number;
}

export interface AdminFunnelStats {
  employmentCohorts?: EmploymentCohort[] | null;
  enrollmentCohorts?: EmploymentCohort[] | null;
  windowDays: number;
  totalUsers: number | null;
  onboardingCompleted: number | null;
  growth: UsageGrowthPoint[];
  onboarding: UsageOnboardingFunnel;
  activation: UsageActivation;
  cohorts: UsageCohort[];
  scans: Record<"running" | "completed" | "failed" | "waiting_for_user", number | null>;
  applications: Record<"cv_ready" | "applied" | "responded" | "interview" | "offer", number | null>;
  cvReadyConversion?: {
    cvReady: number | null;
    terminal: number | null;
    ratePct: number | null;
  };
  applyDrought?: boolean;
  matchesTotal: number | null;
  generatedAt: string;
  warnings: string[];
}

export interface AdminAttentionRun {
  runId: string;
  uid: string;
  type: string;
  startedAt: string | null;
  waitingHours: number | null;
  helpType: string | null;
  helpMessage: string | null;
  boardId: string | null;
  errorDetails: string | null;
}

export interface AdminAttentionReport {
  blocked: AdminAttentionRun[];
  failed24h: AdminAttentionRun[];
  waitingTotal: number;
  generatedAt: string;
  warnings: string[];
}

interface Props {
  funnel: AdminFunnelStats | null;
  attention: AdminAttentionReport | null;
  loading: boolean;
  error: AdminFailure | null;
  onLoad: () => void;
}

type GrowthWindow = "30d" | "90d" | "all";

function sum(parts: (number | null | undefined)[]): number | null {
  let total = 0;
  for (const p of parts) {
    if (p == null) return null;
    total += p;
  }
  return total;
}

function share(part: number | null | undefined, whole: number | null | undefined): string | null {
  if (part == null || whole == null || whole <= 0) return null;
  return pct((part / whole) * 100);
}

function RunRow({ run, kind }: { run: AdminAttentionRun; kind: "blocked" | "failed" }) {
  const urgent = kind === "blocked" && (run.waitingHours ?? 0) >= 24;
  return (
    <li className="px-4 py-3 text-label">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral" size="sm">
          {run.type}
        </Badge>
        <span className="font-mono text-caption text-muted-foreground" title={run.uid}>
          uid {run.uid.slice(0, 8)}
        </span>
        {kind === "blocked" && run.waitingHours != null && (
          <Badge tone={urgent ? "danger" : "warning"} size="sm">
            waiting {run.waitingHours}h
          </Badge>
        )}
        {kind === "failed" && (
          <span className="text-caption text-muted-foreground" title={dateTimeUtc(run.startedAt)}>
            {relativeTime(toMillis(run.startedAt)).label}
          </span>
        )}
        {run.boardId && (
          <Badge tone="outline" size="sm">
            {run.boardId}
          </Badge>
        )}
      </div>
      {kind === "blocked" ? (
        <p className="mt-1.5">
          {run.helpType && <span className="font-medium">[{run.helpType}] </span>}
          {run.helpMessage ?? "No message"}
        </p>
      ) : (
        <p className="mt-1.5 font-mono text-caption text-danger-fg">{run.errorDetails ?? "no error details"}</p>
      )}
      <p className="mt-1 font-mono text-caption text-subtle-foreground">run {run.runId}</p>
    </li>
  );
}

function AttentionList({ title, runs, kind, emptyMessage }: { title: string; runs: AdminAttentionRun[]; kind: "blocked" | "failed"; emptyMessage: string }) {
  return (
    <Block
      title={title}
      className="mb-0"
      action={
        <Badge tone={runs.length > 0 ? "danger" : "neutral"} size="sm">
          {runs.length}
        </Badge>
      }
    >
      {runs.length === 0 ? (
        <EmptyState message={emptyMessage} />
      ) : (
        <Panel padded={false}>
          <ul className="divide-y divide-border">
            {runs.map((run) => (
              <RunRow key={run.runId} run={run} kind={kind} />
            ))}
          </ul>
        </Panel>
      )}
    </Block>
  );
}

/**
 * Onboarding funnel as one sequential brand ramp (admin-12): darker = further
 * down; green only for "active 30d".
 */
function OnboardingFunnelBars({ onboarding, activation }: { onboarding: UsageOnboardingFunnel; activation: UsageActivation }) {
  const rows = [
    { label: "Signup", value: onboarding.signup, color: "color-mix(in srgb, var(--chart-1) 35%, var(--card))" },
    { label: "Reached step 2", value: onboarding.step2Plus, color: "color-mix(in srgb, var(--chart-1) 55%, var(--card))" },
    { label: "Reached step 3", value: onboarding.step3Plus, color: "color-mix(in srgb, var(--chart-1) 75%, var(--card))" },
    { label: "Onboarding done", value: onboarding.completed, color: "var(--chart-1)" },
    { label: "Active (30d)", value: activation.active30d, color: "var(--success-solid)" },
  ];
  const max = Math.max(onboarding.signup, 1);
  return (
    <ul className="space-y-3">
      {rows.map((row, i) => {
        const prev = i === 0 ? row.value : rows[i - 1]!.value;
        const vsSignup = share(row.value, onboarding.signup);
        const vsPrev = share(row.value, prev);
        return (
          <li key={row.label}>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-label">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="tabular-nums">
                {num(row.value)}
                {i > 0 && vsSignup ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · {vsSignup} of signups{vsPrev && vsPrev !== vsSignup ? ` · ${vsPrev} of previous` : ""}
                  </span>
                ) : null}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, (row.value / max) * 100)}%`, backgroundColor: row.color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function cohortColumns(withNeverApplied: boolean, withFollowUps: boolean): Column<EmploymentCohort>[] {
  const n = (key: keyof EmploymentCohort, header: string, title?: string): Column<EmploymentCohort> => ({
    key,
    header,
    title,
    align: "right",
    cell: (c) => num((c[key] as number | undefined) ?? 0),
    sortValue: (c) => (c[key] as number | undefined) ?? 0,
  });
  return [
    { key: "month", header: "Cohort", cell: (c) => <span className="whitespace-nowrap font-medium">{c.month}</span>, sortValue: (c) => c.month },
    n("participants", "Participants"),
    ...(withNeverApplied ? [n("neverApplied", "Never applied")] : []),
    n("mature90", "Observed ≥90d"),
    n("started90", "Started ≤90d"),
    n("meetsMinimums90", "Meets minimums", "Explicit positive review in the first 90 days, excluding temporary work"),
    n("temporary90", "Temporary"),
    n("unknown90", "No dated start"),
    ...(withFollowUps
      ? [
          {
            key: "f30",
            header: "30d working / answered / eligible",
            align: "right" as const,
            cell: (c: EmploymentCohort) => `${c.working30} / ${c.reviewed30} / ${c.eligible30}`,
          },
          {
            key: "f90",
            header: "90d working / answered / eligible",
            align: "right" as const,
            cell: (c: EmploymentCohort) => `${c.working90} / ${c.reviewed90} / ${c.eligible90}`,
          },
        ]
      : []),
  ];
}

export function FunnelSection({ funnel, attention, loading, error, onLoad }: Props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onLoad(); }, []);
  const [growthWindow, setGrowthWindow] = useState<GrowthWindow>("90d");

  const loaded = funnel !== null || attention !== null;
  const warnings = [...(funnel?.warnings ?? []), ...(attention?.warnings ?? [])];

  const appliedStage = funnel
    ? sum([funnel.applications.applied, funnel.applications.responded, funnel.applications.interview, funnel.applications.offer])
    : null;
  const interviewStage = funnel ? sum([funnel.applications.interview, funnel.applications.offer]) : null;
  const offerStage = funnel?.applications.offer ?? null;

  const growthPoints: ChartDatum[] = useMemo(() => {
    const series = funnel?.growth ?? [];
    const slice = growthWindow === "all" ? series : series.slice(-(growthWindow === "30d" ? 30 : 90));
    return slice.map((p) => ({ x: p.date, xLabel: shortDay(p.date), values: { cumulative: p.cumulative, signups: p.signups } }));
  }, [funnel?.growth, growthWindow]);

  const cohortPoints: ChartDatum[] = useMemo(
    () =>
      (funnel?.cohorts ?? []).slice(-16).map((c) => ({
        x: c.week,
        xLabel: shortDay(c.week),
        tooltipLabel: `Week of ${shortDay(c.week)} · ${num(c.signup)} signups`,
        values: {
          step1: c.step1,
          step2: c.step2,
          step3: c.step3,
          doneInactive: Math.max(0, c.completed - c.active30d),
          active: c.active30d,
        },
      })),
    [funnel?.cohorts],
  );

  const onboarding = funnel?.onboarding;
  const activation = funnel?.activation;

  return (
    <div>
      <SectionHeader
        title="Analytics"
        subtitle="Growth and onboarding rebuilt from user docs; scans and applications cover the last 30 days."
        meta={funnel ? <span className="text-caption text-muted-foreground">Generated {dateTimeUtc(funnel.generatedAt)}</span> : undefined}
        action={<LoadButton loading={loading} loaded={loaded} onLoad={onLoad} />}
      />

      {error && <ErrorMessage failure={error} onRetry={onLoad} retrying={loading} />}
      {warnings.length > 0 && (
        <Callout tone="warning" className="mb-4" title="Partial data">
          {warnings.join(" — ")}
        </Callout>
      )}

      {!loaded && loading && <LoadingBlock metrics={5} rows={4} label="Loading analytics" />}
      {!loaded && !loading && !error && <EmptyState message="No analytics loaded." />}

      {/* What needs attention goes first (admin-03). */}
      {funnel?.applyDrought && (
        <Callout tone="danger" className="mb-4" title="Apply drought" live="assertive">
          Completed scans exist but no application reached applied, interview or offer in the last {funnel.windowDays} days.
        </Callout>
      )}
      {attention && (
        <div className="mb-8 grid grid-cols-1 gap-6 @3xl:grid-cols-2">
          <AttentionList
            title={`Blocked runs${attention.waitingTotal > attention.blocked.length ? ` (${attention.waitingTotal} waiting in total)` : ""}`}
            runs={attention.blocked}
            kind="blocked"
            emptyMessage="No runs waiting for user input."
          />
          <AttentionList title="Failed runs (24h)" runs={attention.failed24h} kind="failed" emptyMessage="No failed runs in the last 24h." />
        </div>
      )}

      {funnel && (
        <>
          <MetricGrid cols={5} className="mb-8">
            <Metric label="Signups" value={num(funnel.totalUsers)} sub="All time" />
            <Metric
              label="Onboarding done"
              value={num(funnel.onboardingCompleted)}
              sub={share(funnel.onboardingCompleted, funnel.totalUsers) ? `${share(funnel.onboardingCompleted, funnel.totalUsers)} of signups` : undefined}
            />
            <Metric
              label="Stuck in onboarding"
              value={num(onboarding?.stuck ?? null)}
              tone={(onboarding?.stuck ?? 0) > 0 ? "warn" : "default"}
              hint="Created more than 48 h ago and not done."
              sub={
                onboarding?.stuckByStep
                  ? `Step 1: ${onboarding.stuckByStep.step1} · step 2: ${onboarding.stuckByStep.step2} · step 3: ${onboarding.stuckByStep.step3}`
                  : undefined
              }
            />
            <Metric
              label="Active (7d)"
              value={num(activation?.active7d ?? null)}
              sub={share(activation?.active7d, activation?.completed) ? `${share(activation?.active7d, activation?.completed)} of completed` : "Among completed"}
            />
            <Metric
              label="Active (30d)"
              value={num(activation?.active30d ?? null)}
              sub={share(activation?.active30d, activation?.completed) ? `${share(activation?.active30d, activation?.completed)} of completed` : "Among completed"}
            />
          </MetricGrid>

          <Block
            title="Users over time"
            action={
              <Segmented
                label="Growth window"
                value={growthWindow}
                onChange={setGrowthWindow}
                options={[
                  { value: "30d", label: "30d" },
                  { value: "90d", label: "90d" },
                  { value: "all", label: "All" },
                ]}
              />
            }
          >
            {growthPoints.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 @4xl:grid-cols-2">
                <Panel>
                  <p className="mb-2 text-caption text-muted-foreground">Cumulative users</p>
                  <Chart
                    title="Cumulative users"
                    data={growthPoints}
                    series={[{ key: "cumulative", label: "Users", color: "var(--chart-1)", kind: "line" }]}
                    format={num}
                    formatAxis={compactNum}
                    height={160}
                  />
                </Panel>
                <Panel>
                  <p className="mb-2 text-caption text-muted-foreground">Signups per day</p>
                  <Chart
                    title="Signups per day"
                    data={growthPoints}
                    series={[{ key: "signups", label: "Signups", color: "var(--chart-1)", kind: "bar" }]}
                    format={num}
                    formatAxis={compactNum}
                    height={160}
                    showLastValue={false}
                  />
                </Panel>
              </div>
            ) : (
              <EmptyState message="No dated signups yet — growth is rebuilt from users.createdAt." />
            )}
          </Block>

          <div className="mb-8 grid grid-cols-1 gap-6 @4xl:grid-cols-2">
            <Block title="Onboarding funnel" className="mb-0">
              <Panel>
                {onboarding && activation ? (
                  <OnboardingFunnelBars onboarding={onboarding} activation={activation} />
                ) : (
                  <EmptyState message="Onboarding counts unavailable." />
                )}
              </Panel>
            </Block>
            <Block title="Weekly cohorts" hint="Where each signup week stands today." className="mb-0">
              <Panel>
                {cohortPoints.length > 0 ? (
                  <Chart
                    title="Weekly signup cohorts by current stage"
                    data={cohortPoints}
                    series={[
                      { key: "step1", label: "Step 1", color: "color-mix(in srgb, var(--chart-1) 25%, var(--card))", kind: "bar", stack: true },
                      { key: "step2", label: "Step 2", color: "color-mix(in srgb, var(--chart-1) 45%, var(--card))", kind: "bar", stack: true },
                      { key: "step3", label: "Step 3", color: "color-mix(in srgb, var(--chart-1) 70%, var(--card))", kind: "bar", stack: true },
                      { key: "doneInactive", label: "Done, inactive", color: "var(--chart-1)", kind: "bar", stack: true },
                      { key: "active", label: "Active 30d", color: "var(--success-solid)", kind: "bar", stack: true },
                    ]}
                    format={num}
                    formatAxis={compactNum}
                    height={180}
                  />
                ) : (
                  <EmptyState message="No weekly cohorts yet." />
                )}
              </Panel>
            </Block>
          </div>

          <Block title={`Product usage · ${funnel.windowDays}d`}>
            <MetricGrid cols={6}>
              <Metric
                label="Scans completed"
                value={num(funnel.scans.completed)}
                sub={
                  [
                    funnel.scans.failed != null ? `${num(funnel.scans.failed)} failed` : null,
                    funnel.scans.running != null ? `${num(funnel.scans.running)} running` : null,
                    funnel.scans.waiting_for_user != null ? `${num(funnel.scans.waiting_for_user)} waiting` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
              />
              <Metric label="Matches" value={num(funnel.matchesTotal)} sub="All-time userMatches" />
              <Metric
                label="CV ready → sent"
                value={funnel.cvReadyConversion?.ratePct != null ? pct(funnel.cvReadyConversion.ratePct, 0) : "—"}
                tone={(funnel.cvReadyConversion?.ratePct ?? 0) >= 50 ? "good" : "default"}
                hint="Applications that left cv_ready for a sent or closed state. Target: 50%."
                sub={
                  funnel.cvReadyConversion
                    ? `${num(funnel.cvReadyConversion.terminal)} sent · ${num(funnel.cvReadyConversion.cvReady)} still ready`
                    : undefined
                }
              />
              <Metric label="Applied" value={num(appliedStage)} sub="Reached applied or later" />
              <Metric label="Interview" value={num(interviewStage)} sub={share(interviewStage, appliedStage) ? `${share(interviewStage, appliedStage)} of applied` : undefined} />
              <Metric label="Offer" value={num(offerStage)} sub={share(offerStage, appliedStage) ? `${share(offerStage, appliedStage)} of applied` : undefined} />
            </MetricGrid>
          </Block>

          <Block title="Employment outcomes" hint="Cohorts start at the first recorded application (API or user-declared).">
            {funnel.employmentCohorts?.length ? (
              <DataTable
                caption="Employment outcomes by first-application cohort"
                columns={cohortColumns(false, true)}
                rows={funnel.employmentCohorts}
                rowKey={(c) => c.month}
                defaultSort={{ key: "month", dir: "desc" }}
              />
            ) : (
              <EmptyState message={funnel.employmentCohorts == null ? "Employment outcome data unavailable." : "No recorded first applications yet."} />
            )}
            <details className="mt-2 text-caption text-muted-foreground">
              <summary className="inline-flex min-h-11 cursor-pointer items-center text-label text-foreground md:pointer-fine:min-h-0">How we count</summary>
              <p className="mt-1 max-w-[68ch]">
                Only participants with 90 days of observation enter the 90-day denominator. Outcomes are self-reported; a missing
                report is unknown, not unemployment. &ldquo;Meets minimums&rdquo; requires an explicit positive review within the
                cohort&apos;s first 90 days and excludes temporary work. Late follow-ups do not fill earlier periods; each column counts
                people once and columns may overlap. This is not a measure of all signups or proof of Winche&apos;s impact.
              </p>
            </details>
          </Block>

          <Block title="Enrollment cohorts" hint="Denominator is account creation, including people who never applied.">
            {funnel.enrollmentCohorts?.length ? (
              <DataTable
                caption="Outcomes by enrollment cohort"
                columns={cohortColumns(true, false)}
                rows={funnel.enrollmentCohorts}
                rowKey={(c) => c.month}
                defaultSort={{ key: "month", dir: "desc" }}
              />
            ) : (
              <EmptyState message={funnel.enrollmentCohorts == null ? "Enrollment cohort data unavailable." : "No signups yet."} />
            )}
          </Block>
        </>
      )}
    </div>
  );
}
